import dagre from 'dagre';
import type { Edge, Node } from '@xyflow/react';

// Shared by the layout engine and both expert-card renderers.
export const EXPERT_NODE_WIDTH = 300;
export const EXPERT_NODE_HEIGHT = 170;

export function hasOverlappingNodes(nodes: Node[]): boolean {
  return nodes.some((a, i) => nodes.slice(i + 1).some(b =>
    a.position.x < b.position.x + (b.measured?.width || EXPERT_NODE_WIDTH) + 20 &&
    a.position.x + (a.measured?.width || EXPERT_NODE_WIDTH) + 20 > b.position.x &&
    a.position.y < b.position.y + (b.measured?.height || EXPERT_NODE_HEIGHT) + 20 &&
    a.position.y + (a.measured?.height || EXPERT_NODE_HEIGHT) + 20 > b.position.y
  ));
}

export function getLayoutedElements(nodes: Node[], edges: Edge[], direction: 'LR' | 'TB' = 'TB') {
  const graph = new dagre.graphlib.Graph({ multigraph: true });
  graph.setGraph({ rankdir: direction, ranksep: 100, nodesep: 80, marginx: 30, marginy: 30 });
  graph.setDefaultEdgeLabel(() => ({}));
  for (const node of [...nodes].sort((a, b) => a.id.localeCompare(b.id))) {
    graph.setNode(node.id, { width: node.measured?.width || EXPERT_NODE_WIDTH, height: node.measured?.height || EXPERT_NODE_HEIGHT });
  }
  for (const edge of [...edges].sort((a, b) => a.id.localeCompare(b.id))) {
    if (graph.hasNode(edge.source) && graph.hasNode(edge.target)) graph.setEdge(edge.source, edge.target, {}, edge.id);
  }
  dagre.layout(graph);
  return {
    nodes: nodes.map(node => {
      const box = graph.node(node.id);
      return { ...node, data: { ...node.data, layoutDirection: direction, automaticLayout: true },
        position: { x: box.x - box.width / 2, y: box.y - box.height / 2 } };
    }),
    edges: edges.map(edge => {
      const route = graph.edge({ v: edge.source, w: edge.target, name: edge.id });
      const source = graph.node(edge.source), target = graph.node(edge.target);
      if (!route || !source || !target) return edge;
      return { ...edge, data: { ...edge.data, layoutRoute: {
        points: route.points,
        source: { x: source.x - source.width / 2, y: source.y - source.height / 2 },
        target: { x: target.x - target.width / 2, y: target.y - target.height / 2 },
      } } };
    }),
  };
}

// Visual routes belong with layout preferences, outside behavioral connections.
export function canvasLayoutSettings(nodes: Node[], edges: Edge[]) {
  return { mode: 'manual', direction: nodes[0]?.data.layoutDirection || 'LR',
    routes: Object.fromEntries(edges.filter(edge => edge.data?.layoutRoute).map(edge => [edge.id, edge.data!.layoutRoute])) };
}

export function persistedGraphEdges(edges: Edge[]) {
  return edges.map(edge => {
    if (!edge.data) return edge;
    const { layoutRoute, activeRoutePoints, routeOffset, ...data } = edge.data;
    const { data: _data, ...rest } = edge;
    return Object.keys(data).length ? { ...rest, data } : rest;
  });
}
